import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, EyeOff, LockKeyhole, RefreshCw, ShieldCheck } from "lucide-react";
import { CareerCard } from "../components/CareerCard";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import { PaymentBanner } from "../components/PaymentBanner";
import { FeedbackForm } from "../components/FeedbackForm";
import { ACCESS_PASS_STORAGE_KEY, api } from "../lib/api";
import { DISCLAIMER, HACKATHON_DISCLAIMER } from "../lib/constants";
import { openRazorpayCheckout } from "../lib/razorpay";
import type { ReportResponse } from "../lib/types";

export function PreviewPage() {
  const { reportId = "" } = useParams();
  const [report, setReport] = useState<ReportResponse | null>(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const payment = new URLSearchParams(window.location.search).get("payment");
    if (payment === "pending") setNotice("Payment was received by Razorpay and is still being verified. Refresh this page in a few seconds.");
    if (payment === "cancelled") setNotice("Payment was cancelled. Your free preview is still available.");
  }, []);

  useEffect(() => {
    let active = true;
    const load = () => api.getReport(reportId).then((data) => {
      if (!active) return;
      if (data.fullReport) window.location.assign(`/report/${reportId}`);
      else setReport(data);
    }).catch((caught) => active && setError(caught instanceof Error ? caught.message : "Could not load this preview."));
    void load();
    const timer = window.setInterval(load, 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [reportId]);

  const unlock = async () => {
    setPaying(true); setNotice("");
    try {
      const payment = await api.createPayment(reportId);
      localStorage.setItem(ACCESS_PASS_STORAGE_KEY, payment.accessPassToken);
      if (payment.provider === "razorpay" && payment.razorpay) {
        const success = await openRazorpayCheckout(payment.razorpay);
        if (!success) {
          setNotice("Payment was cancelled. Your free preview remains available.");
          return;
        }
        const verified = await api.verifyRazorpayPayment({
          razorpayOrderId: success.razorpay_order_id,
          razorpayPaymentId: success.razorpay_payment_id,
          razorpaySignature: success.razorpay_signature
        });
        if (verified.status === "paid") window.location.assign(`/report/${reportId}?payment=success`);
        else {
          setReport((current) => current ? { ...current, paymentStatus: "pending" } : current);
          setNotice("Payment is being verified. Refresh this page in a few seconds.");
        }
      } else {
        setReport((current) => current ? { ...current, paymentStatus: "pending" } : current);
        setNotice(payment.message ?? "Your manual payment is waiting for admin verification.");
      }
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : "Could not start payment.");
    } finally { setPaying(false); }
  };

  if (error) return <ErrorState message={error} />;
  if (!report) return <main className="shell px-5"><LoadingState message="Opening your career compass…" /></main>;

  return (
    <main className="px-5 py-12 md:px-10 md:py-20">
      <div className="shell">
        <div className="grid gap-8 lg:grid-cols-[.72fr_1.28fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="flex flex-wrap items-center justify-between gap-3 print:block">
              <p className="eyebrow !mb-0 print:mb-4">Your free preview</p>
              <button className="btn-dark print:hidden" type="button" onClick={() => window.print()}><Download size={17} /> Download free preview PDF</button>
            </div>
            <h1 className="font-display text-6xl leading-[.95] md:text-7xl">Your strengths point somewhere useful.</h1>
            <p className="mt-7 text-lg leading-8 text-ink/65">{report.preview.profileSummary}</p>
            <button className="btn-ghost mt-5 w-full justify-center print:hidden lg:hidden" type="button" onClick={() => window.print()}><Download size={17} /> Save free preview as PDF</button>
            <div className="mt-7 flex items-start gap-3 rounded-2xl bg-white p-4 text-xs leading-5 text-ink/50"><ShieldCheck className="shrink-0 text-moss" size={18} />{DISCLAIMER}</div>
          </div>
          <div className="space-y-5">
            {report.preview.topCareers.map((career) => <CareerCard career={career} key={career.careerId} />)}
          </div>
        </div>
        <section className="relative mt-16 overflow-hidden rounded-[2.5rem] border border-moss/10 bg-white p-6 print:hidden md:p-12">
          <div className="pointer-events-none select-none blur-[7px]">
            <p className="eyebrow">Inside your full report</p>
            <h2 className="section-title">A 30-day plan that starts where you are.</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-3">{report.preview.lockedSections.slice(0, 3).map((section) => <div className="h-36 rounded-3xl bg-cream p-6 font-display text-2xl" key={section}>{section}</div>)}</div>
          </div>
          <div className="absolute inset-0 grid place-items-center bg-gradient-to-b from-white/20 via-white/65 to-white/95">
            <div className="text-center"><div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-moss text-lime"><EyeOff /></div><p className="mt-4 font-display text-3xl">Five sections are still under the fold.</p></div>
          </div>
        </section>
        <div className="mt-7 print:hidden">
          {report.qualityStatus === "verified"
            ? <PaymentBanner loading={paying} pending={report.paymentStatus === "pending"} onUnlock={unlock} />
            : <div className="rounded-[2rem] border border-coral/30 bg-coral/15 p-7 text-center"><RefreshCw className="mx-auto text-coral" /><h3 className="mt-3 font-display text-3xl">AI verification is temporarily delayed</h3><p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-ink/65">This relevant catalog preview is a temporary fallback. Payment is disabled and no pass use will be consumed. We retry Gemini automatically; keep this page open or return shortly.</p></div>}
          {notice && <p className="mt-4 rounded-2xl bg-white p-4 text-center text-sm font-semibold">{notice}</p>}
        </div>
        <div className="mt-8 grid gap-3 print:hidden sm:grid-cols-2 lg:grid-cols-5">{report.preview.lockedSections.map((item) => <div className="flex items-center gap-2 rounded-2xl border border-moss/10 bg-white px-4 py-3 text-xs font-bold text-moss/60" key={item}><LockKeyhole size={14} /> {item}</div>)}</div>
        <FeedbackForm reportId={reportId} />
        <p className="mt-10 border-t border-moss/10 pt-5 text-[10px] leading-4 text-ink/40">{HACKATHON_DISCLAIMER}</p>
      </div>
    </main>
  );
}
